import React, { useEffect, useRef, useState } from 'react';
import { Card } from 'antd';
import { Funnel } from '@antv/g2plot';
import styles from './styles/chart-placeholder.module.scss';

const EnrollmentFunnelChart = ({ title, data }) => {
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(800);
    const [containerHeight, setContainerHeight] = useState(200);

    useEffect(() => {
        // Function to get chart div dimensions
        const updateDimensions = () => {
            if (chartRef.current) {
                // Get the actual rendered width and height of the chart div
                const rect = chartRef.current.getBoundingClientRect();
                const width = rect.width || chartRef.current.offsetWidth || 770;
                const height = rect.height || chartRef.current.offsetHeight || 200;
                if (width > 0) {
                    setContainerWidth(Math.max(width, 300)); // Minimum width of 300px
                }
                if (height > 0) {
                    setContainerHeight(Math.max(height, 150)); // Minimum height of 150px
                }
            }
        };

        // Initial dimension calculation with a small delay to ensure DOM is ready
        const timeout = setTimeout(updateDimensions, 100);

        // ResizeObserver to watch for chart div size changes
        // Use requestAnimationFrame to prevent ResizeObserver loop errors
        let rafId = null;
        const resizeObserver = new ResizeObserver((entries) => {
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            rafId = requestAnimationFrame(() => {
                for (let entry of entries) {
                    // Use the actual rendered dimensions from getBoundingClientRect for accuracy
                    const rect = entry.target.getBoundingClientRect();
                    const width = rect.width || entry.contentRect.width || 770;
                    const height = rect.height || entry.contentRect.height || 200;
                    if (width > 0) {
                        setContainerWidth(Math.max(width, 300)); // Minimum width of 300px
                    }
                    if (height > 0) {
                        setContainerHeight(Math.max(height, 150)); // Minimum height of 150px
                    }
                }
                rafId = null;
            });
        });

        // Observe the chart div itself - it will resize when its container resizes
        const chartElement = chartRef.current;
        if (chartElement) {
            resizeObserver.observe(chartElement);
        }

        return () => {
            clearTimeout(timeout);
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        // Use provided data or fallback to mock data
        const chartData = data || [
            { stage: 'Total Bank Users', value: 70000 },
            { stage: 'Total Opted In', value: 60000 },
            { stage: 'Total Enrolled', value: 35000 },
            { stage: 'Total Active', value: 25000 }
        ];

        if (chartRef.current && containerWidth > 0 && containerHeight > 0) {
            // Get the actual available dimensions to ensure chart fits
            const rect = chartRef.current.getBoundingClientRect();
            const actualWidth = Math.min(containerWidth, rect.width || containerWidth);
            const actualHeight = Math.min(containerHeight, rect.height || containerHeight);

            // If chart already exists, update it instead of recreating
            if (chartInstanceRef.current) {
                chartInstanceRef.current.update({
                    width: actualWidth,
                    height: actualHeight,
                    data: chartData
                });
            } else {
                // Create new chart instance
                chartInstanceRef.current = new Funnel(chartRef.current, {
                    data: chartData,
                    xField: 'stage',
                    yField: 'value',
                    dynamicHeight: true,
                    isTransposed: true,
                    height: actualHeight,
                    width: actualWidth,
                    color: ['#6495ED', '#90EE90', '#708090', '#FFD700'],
                    label: false, // Disable labels to avoid clutter and overlapping
                    conversionTag: false // Hide conversion rate on each stage
                });

                chartInstanceRef.current.render();
            }
        }

        // Cleanup function
        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
                chartInstanceRef.current = null;
            }
        };
    }, [data, containerWidth, containerHeight]);

    return (
        <Card className={styles.chartPlaceholder} title={title}>
            <div className={styles.chartContainer} ref={containerRef}>
                <div ref={chartRef} style={{ width: '100%', height: '100%', minHeight: '150px' }} />
            </div>
        </Card>
    );
};

export default EnrollmentFunnelChart;
